/* eslint-disable @typescript-eslint/naming-convention */
import React, { ReactElement, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './ProductStaticContent.module.scss';
import { TabContext, TabPanel } from '@mui/lab';
import { Box, Divider, Tab, Tabs } from '@mui/material';
import { isBanner, isCardList, isFormattedText, isImageRow, isProductList, isSlider, isVideo } from './typeGuards';
import { ProductsResponse, PDPContent, ProductDetailsItem } from '@services';
import {
  Banner, CardList, FormattedText, ImageRow, ProductList,
  ShippingAndReturns, Slider, Specifications, Video
} from './sections';

const CN = 'product-static-content';
const headerClassName = `${CN}__header`;
const tabsClassName = `${CN}__tabs`;
const tabClassName = `${CN}__tab`;
const tabPanelClassName = `${CN}__tab-panel`;
const tabSelectedClassName = `${CN}__tab--selected`;
const cx = classNames.bind(styles);

interface Props {
  content: PDPContent;
  productLists: Record<string, ProductsResponse> | null;
}

enum TAB_OPTIONS {
  ProductDetails = 'product-details',
  Specifications = 'specifications',
  ShippingAndReturns = 'shipping-and-returns'
}

export const ProductStaticContent: React.FC<Props> = ({
  content: {
    product_details,
    shipping_and_returns,
    specifications
  },
  productLists
}) => {
  const [ tabValue, setTabValue ] = useState(TAB_OPTIONS.ProductDetails);

  const handleTabChange = (event: React.SyntheticEvent, newValue: TAB_OPTIONS): void => {
    setTabValue(newValue);
  };

  const renderProductDetailsItem = (productDetailsItem: ProductDetailsItem): ReactElement | null => {
    if (isFormattedText(productDetailsItem)) {
      return <FormattedText productDetailsItem={ productDetailsItem }/>;
    } else if (isBanner(productDetailsItem)) {
      return <Banner productDetailsItem={ productDetailsItem } />;
    } else if (isCardList(productDetailsItem)) {
      return <CardList productDetailsItem={ productDetailsItem } />;
    } else if (isImageRow(productDetailsItem)) {
      return <ImageRow productDetailsItem={ productDetailsItem } />;
    } else if (isVideo(productDetailsItem)) {
      return <Video productDetailsItem={ productDetailsItem } />;
    } else if (isProductList(productDetailsItem) && productLists?.[productDetailsItem.product_list.title]) {
      return (
        <ProductList
          productDetailsItem={ productDetailsItem }
          products={ productLists?.[productDetailsItem.product_list.title] }
        />
      );
    } else if (isSlider(productDetailsItem)) {
      return <Slider productDetailsItem={ productDetailsItem } />;
    }

    return null;
  };

  return (
    <div className={ cx(CN) }>
      <TabContext value={ tabValue } >
        <Box className={ cx(headerClassName) }>
          <Tabs
            value={ tabValue }
            className={ cx(tabsClassName) }
            onChange={ handleTabChange }
            TabIndicatorProps={ { children: <div className={ cx(tabSelectedClassName) } /> } }
          >
            <Tab className={ cx(tabClassName) } label='Product Details' value={ TAB_OPTIONS.ProductDetails } />
            {specifications && (
              <Tab className={ cx(tabClassName) } label='Specifications' value={ TAB_OPTIONS.Specifications } />
            )}
            {shipping_and_returns && (
              <Tab className={ cx(tabClassName) } label='Shipping and Returns' value={ TAB_OPTIONS.ShippingAndReturns } />
            )}
          </Tabs>
        </Box>
        <Divider/>
        <TabPanel value={ TAB_OPTIONS.ProductDetails } className={ cx(tabPanelClassName) }>
          {product_details.map(renderProductDetailsItem)}
        </TabPanel>
        {specifications && (
          <TabPanel value={ TAB_OPTIONS.Specifications } className={ cx(tabPanelClassName) }>
            <Specifications specifications={ specifications } />
          </TabPanel>
        )}
        {shipping_and_returns && (
          <TabPanel value={ TAB_OPTIONS.ShippingAndReturns } className={ cx(tabPanelClassName) }>
            <ShippingAndReturns shippingAndReturns={ shipping_and_returns } />
          </TabPanel>
        )}
      </TabContext>
    </div>
  );
};
